import { Box, Divider, Flex, Grid, IconButton, Image, Text, Drawer, DrawerBody, DrawerHeader, DrawerOverlay, DrawerContent, DrawerCloseButton, FormControl, FormLabel, Input, Button, useDisclosure, DrawerFooter, FormErrorMessage, Checkbox, Modal, ModalOverlay, ModalContent, ModalCloseButton, ModalBody, PinInput, PinInputField, ModalHeader, Select, } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Deduction, EmployeerDetail, Salary } from "./moda";
import { useFetch } from "../../../../../hooks/useFetch";
import { AlertCard, CustomToast } from "../../../../../components";
import EditLogo from "../../../../../assets/images/EditLogo.svg";
import { useForm, Controller, Resolver } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import SalaryLogo from "../../../../../assets/images/SalaryLogo.png";
import * as yup from "yup";
import { useAuth } from "../../../../../hooks/useAuth";
import { calculateEmployeeSalary, calculateInternSalary } from "../../../../../utilities/salaryCalculator";
import { HrlenseInput } from "../../../../../components/Input/Input";
import { Tooltip } from "@chakra-ui/react";
import { TimeIcon } from "@chakra-ui/icons";
import { showDate, showDateTime } from "../../../../../utilities/helper";

type props = {
  employeeId: number | null;
  data?: EmployeerDetail;
  onComplete?: any;
};

export type SalaryForm = {
  salary_Per_Month: number;
  gross_Salary_per_anum?: number;
  basic_salary?: number;
  hra?: number;
  conveyance?: number;
  total_Deduction?: number;
  net_Salary?: number;
  mealAllowance?: boolean;
  transportAllowance?: boolean;
  healthInsurance?: boolean;
  basic_Salary_for_pf?: number;
  employeePF?: number;
  employerPF?: number;
  esicEmployee?: number;
  esicEmployer?: number;
  monthlyCTC?: number;
  annualCTC?: number;
  netTakeHomeSalary?: number;
  esicApplicable?: boolean;
  includePf?: boolean;
  includeEsic?: boolean;
  employement_Type: "EMPLOYEE" | "INTERN";
  intern_Payment_Type?: "PAID" | "UNPAID" | "";
  enableElCl?: boolean;
};

const salaryCompensationSchema = yup.object({
  employement_Type: yup.string().oneOf(["EMPLOYEE", "INTERN"]).required("Employment type is required"),

  intern_Payment_Type: yup.string().when("employement_Type", {
    is: (val: string) => val === "INTERN",
    then: (schema) => schema.required("Intern payment type is required"),
    otherwise: (schema) =>
      schema.strip(),
  }),

  salary_Per_Month: yup.number().when(["employement_Type", "intern_Payment_Type"], {
    is: (type: string, internType: string) => type === "EMPLOYEE" || internType === "PAID",
    then: (schema) => schema.typeError("Salary must be a number").required("Salary is required"),
    otherwise: (schema) => schema.strip(),
  }
  ),


});

const SalaryDetail = ({ employeeId, data, onComplete }: props) => {
  const [loading, setLoading] = useState(false);
  const { addToast } = CustomToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { fetchApi } = useFetch(addToast);
  const [employeerDetail, setEmployeerDetail] = useState<EmployeerDetail | undefined>(data);
  const [salaryRule, setSalaryRule] = useState<any>(null);
  const [benfits, setBenfits] = useState<any | null>(null);
  const [isInitializing, setIsInitializing] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{ title: string, description: string; colorScheme: string; onConfirm: () => void; } | null>(null);
  const [sendOtp, setSendOtp] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpbox, setOtpbox] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const { hasPermission, loginObj } = useAuth();
  const canViewOthers = hasPermission("Employee", "showCriticalField");
  const loggedInEmpId = loginObj?.employeeId;
  const isOwnProfile = loggedInEmpId === employeeId;
  const [includePF, setIncludePF] = useState(true);
  const [includeESIC, setIncludeESIC] = useState(true);
  const [calculationRuleWithoutPF, setCalculationRuleWithoutPF] = useState<any | null>(null)

  const canEditSalary = hasPermission("Employee", "UpdateSalaryDetail") && hasPermission("Employee", "showCriticalField");


  const { isOpen: isHistoryOpen, onOpen: onHistoryOpen, onClose: onHistoryClose } = useDisclosure();
  const [historyLogs, setHistoryLogs] = useState<any[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);


  const { control, handleSubmit, formState: { errors }, watch, reset, setValue, } = useForm<SalaryForm>({
    resolver: yupResolver(salaryCompensationSchema) as Resolver<SalaryForm>,
    defaultValues: data as SalaryForm
  });

  useEffect(() => {
    if (data) {
      setEmployeerDetail(data);
      setIncludeESIC(data.includeEsic ?? true);
      setIncludePF(data.includePf ?? true);
    }
  }, [data]);

  const grossMonthValue = parseFloat(watch("salary_Per_Month")?.toString() || "0");

  const getPercentage = (value?: number) => {
    const gross = Number(watch("salary_Per_Month"));

    if (!gross || !value) return "";

    const percentage = ((Number(value) / gross) * 100).toFixed(2);

    return `(${parseFloat(percentage)}%)`;
  };

  const getCurrentSalaryRule = async (employementType: string, internType?: string) => {
    const params = new URLSearchParams({
      branchId: String(employeerDetail?.branch_Id ?? 0),
      departmentId: String(employeerDetail?.department_Id ?? 0),
      employementType,
      internPaymentType: employementType === "INTERN" ? internType ?? "" : "",
    });

    return await fetchApi("SalaryCalculationSettings/rules", "GET", "", params.toString());
  };

  const onSubmit = async (data: any) => {
    setLoading(true);
    const currentRule = await getCurrentSalaryRule(data.employement_Type, data.intern_Payment_Type);
    if (data.employement_Type === "EMPLOYEE" && data.salary_Per_Month < Number(currentRule?.min_Gross_Salary)) {
      addToast({ status: "error", message: `Minimum salary for Employee is ₹${currentRule?.min_Gross_Salary}`, });
      setLoading(false);
      return;
    }
    const payload = buildSalaryPayload(data);

    let endpoint = `Hrlense_Employee/empSalary/${employeeId}`;
    const response = await fetchApi(endpoint, "PUT", payload, null, "Salary Updated Successfully");
    if (response) {
      onComplete();
      onClose();
    }
    setLoading(false);
  };


  const buildSalaryPayload = (data: SalaryForm) => ({
    EmpId: employeeId,
    Gross_Salary: data.salary_Per_Month ?? 0,
    Basic_Salary: data.basic_salary ?? 0,
    Basic_Salary_For_PF: data.basic_Salary_for_pf ?? data.basic_salary ?? 0,
    includePF: includePF,
    IncludeESIC: includeESIC,
    EmployeePF: data.employeePF ?? 0,
    EmployerPF: data.employerPF ?? 0,
    Esic_Applicable: data.esicEmployee !== 0 ? true : false,
    EsicEmployee: data.esicEmployee ?? 0,
    EsicEmployer: data.esicEmployer ?? 0,
    Meal_Allowance_Applied: !!data.mealAllowance,
    Meal_Allowance_Amount: data.mealAllowance ? benfits?.meal_Allowance ?? 0 : 0,
    Transport_Allowance_Applied: !!data.transportAllowance,
    Transport_Allowance_Amount: data.transportAllowance ? benfits?.transportation_Allowance ?? 0 : 0,
    Health_Insurance_Applied: !!data.healthInsurance,
    Health_Insurance_Amount: data.healthInsurance ? benfits?.health_Insurance_Coverage ?? 0 : 0,
    MonthlyCTC: data.monthlyCTC ?? 0,
    AnnualCTC: data.annualCTC ?? 0,
    Net_Take_Home_Salary: data.netTakeHomeSalary ?? 0,
    Hra: data.hra ?? 0,
    Conveyance: data.conveyance ?? 0,
    employement_Type: data.employement_Type,
    intern_Payment_Type: data.intern_Payment_Type,
    // EL/CL is only valid for a PAID intern; everything else is sent as false
    EnableElCl: data.employement_Type === "INTERN" && data.intern_Payment_Type === "PAID" ? !!data.enableElCl : false,
  });


  const getFormDefaults = (d: EmployeerDetail): SalaryForm => ({
    salary_Per_Month: d.gross_Salary_Per_Month ?? 0,
    basic_salary: d.basic_salary ?? 0,
    basic_Salary_for_pf: d.basic_Salary_for_pf ?? d.basic_salary ?? 0,
    hra: d.hra ?? 0,
    includePf: d.includePf,
    conveyance: d.conveyance ?? 0,
    employeePF: d.employeePF ?? 0,
    employerPF: d.employerPF ?? 0,
    esicEmployee: d.esicEmployee ?? 0,
    esicEmployer: d.esicEmployer ?? 0,
    mealAllowance: d.mealAllowance,
    transportAllowance: d.transportAllowance,
    healthInsurance: d.healthInsurance,
    monthlyCTC: d.monthlyCTC ?? 0,
    annualCTC: d.annualCTC ?? 0,
    netTakeHomeSalary: d.netTakeHomeSalary ?? 0,
    employement_Type: d.employement_Type,
    intern_Payment_Type: d.intern_Payment_Type,
    enableElCl: d.enableElCl ?? false,
  });

  function handleEditClick() {
    if (!data) return;
    setIsInitializing(true);
    setIncludePF(data?.includePf ?? true);
    setIncludeESIC(data?.includeEsic ?? true);
    reset(getFormDefaults(data));
    onOpen();
    setTimeout(() => setIsInitializing(false), 0);
  }

  const handleReset = () => {
    if (!data) return;
    setIsInitializing(true);
    setAlertConfig(null);
    setIncludePF(data.includePf ?? true);
    setIncludeESIC(data.includeEsic ?? true);
    reset(getFormDefaults(data));
    setTimeout(() => setIsInitializing(false), 0);
  };
  const fetchSalaryHistory = async () => {
    setHistoryLoading(true);
    const response = await fetchApi(`Hrlense_EmployeeStatus/salaryLogs/${employeeId}`, "GET", null, "", "");
    if (response?.data) setHistoryLogs(response.data);
    setHistoryLoading(false);
    onHistoryOpen();
  };

  const employement_Type = watch("employement_Type");
  const intern_Payment_Type = watch("intern_Payment_Type");

  const isIntern = employement_Type === "INTERN";
  const isPaidIntern = employement_Type === "INTERN" && intern_Payment_Type === "PAID";

  // Clear EL/CL as soon as the person is no longer a paid intern
  useEffect(() => {
    if (!isPaidIntern) setValue("enableElCl", false);
  }, [isPaidIntern, setValue]);

  const loadSalaryRule = async () => {
    if (isIntern && !intern_Payment_Type) {
      setSalaryRule(null);
      return;
    }

    const params = new URLSearchParams({
      branchId: String(employeerDetail?.branch_Id ?? 0),
      departmentId: String(employeerDetail?.department_Id ?? 0),
      employementType: employement_Type ?? "EMPLOYEE",
      internPaymentType: employement_Type === "INTERN" ? intern_Payment_Type ?? "" : "",
    });
    const response = await fetchApi("SalaryCalculationSettings/rules", "GET", "", params.toString());
    if (response) {
      setSalaryRule(response || null);
    } else {
      setSalaryRule(null);
    }
  }

  const loadBenfit = async () => {
    const response = await fetchApi("DropDown/benefitList", "GET", null, `branch_Id=${employeerDetail?.branch_Id ?? 0}`);
    if (response) setBenfits(response);
  }


  const loadSalaryCalculationRuleWithoutPF = async () => {
    const response = await fetchApi("SalaryCalculationSettings/GetSalarySlabBranchWise", "GET", null, `branch_Id=${employeerDetail?.branch_Id ?? 0}`)
    if (response) setCalculationRuleWithoutPF(response)
  }


  useEffect(() => {
    if (employeerDetail) {
      loadSalaryRule();
      loadBenfit();
      loadSalaryCalculationRuleWithoutPF();
    }
  }, [employeerDetail]);


  const mealAllowance = watch("mealAllowance");
  const transportAllowance = watch("transportAllowance");
  const healthInsurance = watch("healthInsurance");


  useEffect(() => {
    if (employeerDetail) loadSalaryRule();
  }, [employeerDetail, employement_Type, intern_Payment_Type]);


  useEffect(() => {
    if (isInitializing) return;

    const isSameAsDb =
      !!data &&
      grossMonthValue === Number(data.gross_Salary_Per_Month) &&
      employement_Type === data.employement_Type &&
      (intern_Payment_Type || "") === (data.intern_Payment_Type || "") &&
      includePF === (data.includePf ?? true) &&
      includeESIC === (data.includeEsic ?? true) &&
      !!mealAllowance === !!data.mealAllowance &&
      !!transportAllowance === !!data.transportAllowance &&
      !!healthInsurance === !!data.healthInsurance;

    if (isSameAsDb) {
      reset(getFormDefaults(data!));   // inputs match DB → show DB values, don't recalculate
      return;
    }

    if (!grossMonthValue || !salaryRule || grossMonthValue < salaryRule?.min_Gross_Salary) {
      setValue("basic_salary", 0);
      setValue("basic_Salary_for_pf", 0);
      setValue("hra", 0);
      setValue("conveyance", 0);

      setValue("employeePF", 0);
      setValue("employerPF", 0);
      setValue("esicEmployee", 0);
      setValue("esicEmployer", 0);

      setValue("monthlyCTC", 0);
      setValue("annualCTC", 0);
      setValue("netTakeHomeSalary", 0);

      setValue("healthInsurance", false);
      setValue("mealAllowance", false);
      setValue("transportAllowance", false);

      return;
    }

    // const result = calculateEmployeeSalary({ gross: grossMonthValue, rule: salaryRule, benefits: benfits, options: { meal: mealAllowance, transport: transportAllowance, health: healthInsurance, includePF: includePF }, withoutPfCalculationRule: calculationRuleWithoutPF });

    const result = employement_Type === "INTERN" ?
      calculateInternSalary({ gross: grossMonthValue, rule: salaryRule, benefits: benfits, options: { meal: mealAllowance, transport: transportAllowance, health: healthInsurance, includePF, includeESIC } })
      : calculateEmployeeSalary({ gross: grossMonthValue, rule: salaryRule, benefits: benfits, options: { meal: mealAllowance, transport: transportAllowance, health: healthInsurance, includePF, includeESIC }, withoutPfCalculationRule: calculationRuleWithoutPF });

    if (!result) return;

    setValue("basic_salary", result.basic);
    setValue("basic_Salary_for_pf", result.pfBasic);
    setValue("hra", result.hra);
    setValue("conveyance", result.conveyance);

    setValue("employeePF", result.employeePF);
    setValue("employerPF", result.employerPF);
    setValue("esicEmployee", result.esicEmployee);
    setValue("esicEmployer", result.esicEmployer);

    setValue("monthlyCTC", result.monthlyCTC);
    setValue("annualCTC", Math.round(result.annualCTC));
    setValue("netTakeHomeSalary", result.netSalary);

    if (grossMonthValue <= salaryRule.esiC_Wage_Limit) {
      setValue("healthInsurance", false);
    }

  }, [grossMonthValue, salaryRule, mealAllowance, transportAllowance, healthInsurance, benfits, setValue, includePF, includeESIC, data, employement_Type, intern_Payment_Type]);


  const isSalaryRuleMissing = !salaryRule;


  const sendOtpforIdentity = async () => {
    setSendOtp(true);
    const res = await fetchApi("Tds_Slab/otpforsecure", "POST", { employee_Id: employeeId, purpose: "Salary Details" }, null, "");
    if (res) {
      setSendOtp(false);
      setOtpbox(true);
      addToast({ message: res.message, status: "success" });
    }
    if (!res) {
      setSendOtp(false);
      onClose();

    }
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) {
      addToast({ message: "Enter 6 digit Verification Code", status: "error" });
      return;
    }
    setVerifying(true);
    const res = await fetchApi("Tds_Slab/verifyotp", "POST",
      { purpose: "Salary Details", otp }
    );
    if (res) { setVerifying(false); setOtpbox(false); setOtpVerified(true); setOtp(""); addToast({ message: res.message, status: "success" }); }
    if (!res) { setVerifying(false); setOtp(""); }
  };


  const getDisplayValue = (key: any, value?: any) => {
    if (!value) return "-";
    if (canViewOthers) {
      return value;
    }
    if (isOwnProfile && otpVerified) {
      return value;
    }
    if (isOwnProfile) {
      return "********"
    }
    return "********";
  };


  useEffect(() => {
    if (!grossMonthValue || !salaryRule) return;
    if (grossMonthValue <= salaryRule.pf_Deduction_Threshold) {
      setIncludePF(true);
    }
  }, [grossMonthValue, salaryRule]);

  useEffect(() => {
    if (employement_Type === "INTERN" && intern_Payment_Type === "UNPAID") {
      setValue("basic_salary", 0);
      setValue("basic_Salary_for_pf", 0);
      setValue("hra", 0);
      setValue("conveyance", 0);

      setValue("employeePF", 0);
      setValue("employerPF", 0);
      setValue("esicEmployee", 0);
      setValue("esicEmployer", 0);

      setValue("monthlyCTC", 0);
      setValue("annualCTC", 0);
      setValue("netTakeHomeSalary", 0);
    }
  }, [employement_Type, intern_Payment_Type, setValue]);

  const handleGrossSalaryBlur = (gross: number) => {
    if (salaryRule && employement_Type === "EMPLOYEE" && gross > 0 && gross < Number(salaryRule.min_Gross_Salary)) {
      setAlertConfig({
        title: "Minimum Salary Validation",
        colorScheme: "orange",
        description: `The minimum Gross Salary configured is ₹${salaryRule.min_Gross_Salary}.
          You entered Gross Salary ₹${gross}.

         Do you want to convert this employee to a Paid Intern?`,
        onConfirm: () => {
          setValue("employement_Type", "INTERN");
          setValue("intern_Payment_Type", "PAID");
        }
      });
    }
  };

  const handleInternAlertClose = () => {
    const gross = Number(watch("salary_Per_Month"));
    if (employement_Type === "EMPLOYEE" && gross > 0 && gross < Number(salaryRule?.min_Basic)) {
      setValue("salary_Per_Month", 0);

      setValue("basic_salary", 0);
      setValue("basic_Salary_for_pf", 0);
      setValue("hra", 0);
      setValue("conveyance", 0);

      setValue("employeePF", 0);
      setValue("employerPF", 0);
      setValue("esicEmployee", 0);
      setValue("esicEmployer", 0);

      setValue("monthlyCTC", 0);
      setValue("annualCTC", 0);
      setValue("netTakeHomeSalary", 0);
    }

    setAlertConfig(null);
  };

  useEffect(() => {
    const gross = Number(watch("salary_Per_Month"));

    if (employement_Type === "EMPLOYEE" && salaryRule && gross > 0 && gross < Number(salaryRule.min_Gross_Salary)) {
      setAlertConfig({
        title: "Minimum Salary Validation",
        colorScheme: "orange",
        description: `The minimum gross Salary configured is ₹${salaryRule.min_Gross_Salary}.
You entered Gross Salary ₹${gross}.

Do you want to convert this employee to a Paid Intern?`,
        onConfirm: () => {
          setValue("employement_Type", "INTERN");
          setValue("intern_Payment_Type", "PAID");
        },
      });
    }
  }, [salaryRule]);


  return (
    <>
      <Box pt={2} mb={6} className="main">
        <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
          <Flex align="center" gap={3}>
            <Image src={SalaryLogo} alt="Salary" />
            <Text className="font-poppins text_sm text_medium col">Salary</Text>
          </Flex>
          <Flex align="center" gap={3}>

            {hasPermission("Employee", "ViewSalaryLogHistory") &&
              <Tooltip label="View Salary history" placement="top" hasArrow>
                <IconButton aria-label="Salary History" icon={<TimeIcon boxSize="18px" color="green" />} variant="none" onClick={fetchSalaryHistory} />
              </Tooltip>}

            {!canViewOthers && isOwnProfile && !otpVerified && (
              <Button size="xs" variant="link" colorScheme="blue"
                onClick={() =>
                  setAlertConfig({
                    title: "Access Confirmation",
                    description: "For security purposes, a verification code is required to access full details. Proceed to receive the code?",
                    colorScheme: "green", onConfirm: () => sendOtpforIdentity(),
                  })
                }
              >
                View
              </Button>
            )}
            {canEditSalary && (<IconButton aria-label="Edit" icon={<Image src={EditLogo} />} variant="none" onClick={() => handleEditClick()} />)}
          </Flex>
        </Flex>
        <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
          {/* {Salary.map((data, index) => {
            const rawvalue = employeerDetail?.[data?.key];
            const displayValue = getDisplayValue(data.key, rawvalue);
            return (
              <Grid key={index} templateColumns="1fr 1fr" alignItems="center">
                <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)" mb={3} >{data?.label}</Text>
                <Text className="font-poppins text_sm text_medium col">{displayValue}</Text>
              </Grid>
            );
          }

          )} */}

          {Salary.filter(item => {
            if (item.key === "intern_Payment_Type" && employeerDetail?.employement_Type !== "INTERN") {
              return false;
            }
            // EL/CL row only makes sense for a PAID intern
            if (
              item.key === "enableElCl" &&
              !(employeerDetail?.employement_Type === "INTERN" && employeerDetail?.intern_Payment_Type === "PAID")
            ) {
              return false;
            }
            return true;
          }).map((data, index) => {
            // booleans are falsy-masked as "-" by getDisplayValue, so convert to Yes/No first
            const rawvalue =
              data.key === "enableElCl"
                ? (employeerDetail?.enableElCl ? "Yes" : "No")
                : employeerDetail?.[data.key];
            const displayValue = getDisplayValue(data.key, rawvalue);

            return (
              <Grid key={index} templateColumns="1fr 1fr" alignItems="center" mb={2}>
                <Text
                  className="font-poppins text_sm text_medium"
                  color="rgba(102, 102, 102, 1)"

                >
                  {data.label}
                </Text>

                <Text className="font-poppins text_sm text_medium col">
                  {displayValue}
                </Text>
              </Grid>
            );
          })}
        </Box>
        <Flex justify="space-between" align="center" mb={2} pl={5} pr={5}>
          <Flex align="center" gap={3}>
            <Text className="font-poppins text_sm text_medium col">Deductions</Text>
          </Flex>
        </Flex>

        <Box py={2} px={10} bg="#FFFFFF" borderRadius={8}>
          {Deduction.map((data, index) => {
            const rawvalue = employeerDetail?.[data?.key];
            const displayValue = getDisplayValue(data.key, rawvalue);
            return (
              <Grid key={index} templateColumns="1fr 1fr" alignItems="center" mb={2} gap={2}>
                <Text className="font-poppins text_sm text_medium" color="rgba(102, 102, 102, 1)" wordBreak="break-word" > {data?.label} </Text>
                <Text className="font-poppins text_sm text_medium col"> {displayValue} </Text>
              </Grid>
            );
          }

          )}
        </Box>

      </Box>
      <AlertCard title={alertConfig?.title} isLoading={sendOtp} isOpen={!!alertConfig} onClose={() => handleInternAlertClose()} onConfirm={() => { if (alertConfig?.onConfirm) alertConfig.onConfirm(); setAlertConfig(null); }} description={alertConfig?.description || ''} colorScheme={alertConfig?.colorScheme || 'red'}
      />
      <Modal isOpen={otpbox} onClose={() => setOtpbox(false)} closeOnOverlayClick={false} closeOnEsc={false} isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="12px" p={6}>
          <ModalCloseButton />
          <ModalBody textAlign="center">
            <Text fontSize="xl" fontWeight="600" mb={2}> OTP Verification </Text>
            <Text color="gray.500" mb={6}>Please enter the 6-digit verification code sent to your registered official email address.</Text>
            <Flex justify="center" gap={3} mb={6}>
              <PinInput otp value={otp} onChange={setOtp}>
                {[...Array(6)].map((_, i) => (
                  <PinInputField key={i} />
                ))}
              </PinInput>
            </Flex>
            <Button isDisabled={verifying} width="100%" colorScheme="green" onClick={verifyOtp} >Verify Code</Button>
          </ModalBody>
        </ModalContent>
      </Modal>
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
        <DrawerOverlay />
        <form onSubmit={handleSubmit(onSubmit)}>
          <DrawerContent display="flex" flexDirection="column" height="100%">
            <DrawerCloseButton />
            <DrawerHeader>Update Salary Detail</DrawerHeader>
            <DrawerBody flex={1} overflow="auto">
              <Box m={4}>
                <Grid templateColumns="repeat(3, 1fr)" gap={4}>
                  <FormControl>
                    <FormLabel>Employment Type</FormLabel>
                    <Controller name="employement_Type" control={control} render={({ field }) => (
                      <Select {...field}>
                        <option value="EMPLOYEE">Employee</option>
                        <option value="INTERN">Intern</option>
                      </Select>
                    )} />
                  </FormControl>

                  {employement_Type === "INTERN" && (
                    <FormControl>
                      <FormLabel>Intern Type</FormLabel>
                      <Controller name="intern_Payment_Type" control={control} render={({ field }) => (
                        <Select {...field}>
                          <option value="">Select</option>
                          <option value="PAID">Paid Intern</option>
                          <option value="UNPAID">Unpaid Intern</option>
                        </Select>
                      )} />
                    </FormControl>
                  )}

                  <FormControl mb={4} isInvalid={!!errors.salary_Per_Month}>
                    <FormLabel whiteSpace="nowrap">Gross Salary Per Month</FormLabel>
                    {/* <Controller name="salary_Per_Month" control={control} render={({ field }) => <Input type="number" {...field} isDisabled={isSalaryRuleMissing} />} /> */}

                    <Controller name="salary_Per_Month" control={control} render={({ field }) => (
                      <Input type="number" {...field} isDisabled={isSalaryRuleMissing} onBlur={(e) => {
                        field.onBlur();
                        handleGrossSalaryBlur(Number(e.target.value));
                      }}
                      />
                    )}
                    />
                    <FormErrorMessage> {errors.salary_Per_Month?.message} </FormErrorMessage>
                  </FormControl>
                  <HrlenseInput disable={isSalaryRuleMissing} name="basic_salary" label={`Basic Salary ${getPercentage(watch("basic_salary"))}`} control={control} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} name="basic_Salary_for_pf" label="Basic salary for PF" type="number" control={control} placeholder="Enter PF Basic" readOnly />

                  <Controller name="mealAllowance" control={control} render={({ field }) => (
                    <Checkbox disabled={!grossMonthValue} isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref}  >
                      Meal Allowance {benfits?.meal_Allowance ? ` ₹(${benfits.meal_Allowance})` : ''} </Checkbox>)} />
                  <Controller name="transportAllowance" control={control} render={({ field }) => (
                    <Checkbox disabled={!grossMonthValue} isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} >
                      Transport Allowance {benfits?.transportation_Allowance ? ` ₹(${benfits.transportation_Allowance})` : ''}</Checkbox>)} />


                  {grossMonthValue && grossMonthValue > salaryRule?.esiC_Wage_Limit && (<Controller name="healthInsurance" control={control} render={({ field }) => (
                    <Checkbox disabled={!grossMonthValue} isChecked={field.value} onChange={(e) => field.onChange(e.target.checked)} onBlur={field.onBlur} name={field.name} ref={field.ref} >
                      Health Insurance {benfits?.health_Insurance_Coverage ? ` ₹(${benfits.health_Insurance_Coverage})` : ''}</Checkbox>)} />
                  )}

                  <Checkbox isChecked={includePF} onChange={(e) => setIncludePF(e.target.checked)} isDisabled={!grossMonthValue || isSalaryRuleMissing || grossMonthValue <= salaryRule?.pf_Deduction_Threshold}>Include PF</Checkbox>

                  {/* {salaryRule?.is_ESIC_Required && grossMonthValue > 0 && grossMonthValue <= salaryRule?.esiC_Wage_Limit && (
                    <Checkbox
                      isChecked={includeESIC}
                      onChange={(e) => setIncludeESIC(e.target.checked)}
                      isDisabled={!grossMonthValue || isSalaryRuleMissing}
                    >
                      Include ESIC
                    </Checkbox>
                  )} */}

                  {salaryRule?.is_ESIC_Required && grossMonthValue > 0 && (grossMonthValue <= salaryRule?.esiC_Wage_Limit || includeESIC) && (
                    <Checkbox
                      isChecked={includeESIC}
                      onChange={(e) => setIncludeESIC(e.target.checked)}
                      isDisabled={!grossMonthValue || isSalaryRuleMissing}
                    >
                      Include ESIC
                      {grossMonthValue > salaryRule?.esiC_Wage_Limit && (
                        <Text as="span" fontSize="xs" color="orange.500" ml={2}>
                          (over wage limit — will auto-stop at next ESIC cycle)
                        </Text>
                      )}
                    </Checkbox>
                  )}

                  {/* EL/CL is available only for paid interns */}
                  {isPaidIntern && (
                    <Controller name="enableElCl" control={control} render={({ field }) => (
                      <Checkbox
                        isChecked={!!field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        onBlur={field.onBlur}
                        name={field.name}
                        ref={field.ref}
                      >
                        Enable EL/CL
                      </Checkbox>
                    )} />
                  )}

                  <HrlenseInput disable={isSalaryRuleMissing} name="employeePF" label={`Employee PF (12%)`} control={control} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} name="employerPF" label={`Employer PF (12%)`} control={control} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} name="esicEmployee" label={`Employee ESIC ${getPercentage(watch("esicEmployee"))}`} control={control} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} name="esicEmployer" label={`Employer ESIC  ${getPercentage(watch("esicEmployer"))}`} control={control} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} isRequired name="hra" label={`HRA ${getPercentage(watch("hra"))}`} control={control} errors={errors} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} isRequired name="conveyance" label={`Conveyance ${getPercentage(watch("conveyance"))}`} control={control} errors={errors} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} name="monthlyCTC" label="Monthly CTC" control={control} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} name="annualCTC" label="Annual CTC" control={control} readOnly />
                  <HrlenseInput disable={isSalaryRuleMissing} name="netTakeHomeSalary" label="Net Salary" control={control} readOnly />
                </Grid>
              </Box>
            </DrawerBody>
            <DrawerFooter gap={3}>
              <Button type="button" variant="outline" onClick={handleReset}>Reset</Button>
              <Button className="btn_theme" type="submit" isLoading={loading}>Update Details</Button>
            </DrawerFooter>
          </DrawerContent>
        </form>
      </Drawer>

      <Modal isOpen={isHistoryOpen} onClose={onHistoryClose} size="4xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius={12}>
          <ModalHeader>Salary History</ModalHeader>
          <ModalCloseButton />
          <ModalBody px={6} py={4} >
            {historyLoading ? (
              <Text textAlign="center" py={6}>Loading...</Text>
            ) : historyLogs.length === 0 ? (
              <Text textAlign="center" py={6}>No history found</Text>
            ) : (

              <Box position="relative">
                <Box position="absolute" left="5px" top="0" bottom="0" w="2px" bg="gray.200" />
                {historyLogs.map((log: any, i: number) => {
                  const FIELDS = [
                    { label: "Gross", old: log.old_Gross, next: log.new_Gross },
                    { label: "Basic", old: log.old_Basic, next: log.new_Basic },
                    { label: "HRA", old: log.old_HRA, next: log.new_HRA },
                    { label: "Conveyance", old: log.old_Conveyance, next: log.new_Conveyance },
                    { label: "Employee PF", old: log.old_EmployeePF, next: log.new_EmployeePF },
                    { label: "Employer PF", old: log.old_EmployerPF, next: log.new_EmployerPF },
                    { label: "ESIC Employee", old: log.old_ESIC_Employee, next: log.new_ESIC_Employee },
                    { label: "ESIC Employer", old: log.old_ESIC_Employer, next: log.new_ESIC_Employer },
                    { label: "Net Salary", old: log.old_NetSalary, next: log.new_NetSalary },
                    { label: "Monthly CTC", old: log.old_MonthlyCTC, next: log.new_MonthlyCTC },
                  ].filter(f => Number(f.old) !== Number(f.next));

                  return (
                    <Box key={i} pl={6} mb={6} position="relative">
                      <Flex align="center" mb={2}>
                        <Box position="absolute" left="0px" w="10px" h="10px" bg="blue.500" borderRadius="full" />
                        <Text fontWeight="600" ml={4}>Update #{historyLogs.length - i}</Text>
                        <Text ml={3} fontSize="12px" color="gray.500">{showDateTime(log.created_At)}</Text>
                        <Text ml={3} fontSize="12px" color="gray.500">{log.createdByName}</Text>
                        {i === 0 && (<Text ml={2} fontSize="10px" color="green.500">(Latest)</Text>)}
                      </Flex>


                      <Box pl={4}>
                        {FIELDS.length === 0 ? (
                          <Text fontSize="13px" color="gray.400">No changes detected</Text>
                        ) : (
                          FIELDS.map((f, j) => (
                            <Flex key={j} align="center" gap={3} mb={2} p={2} bg="gray.50" borderRadius="md">
                              <Text minW="130px" fontSize="12px" fontWeight="600">{f.label}</Text>
                              <Text color="red.400" textDecoration="line-through">₹ {Number(f.old).toLocaleString("en-IN")}</Text>
                              <Text color="gray.400">→</Text>
                              <Text color="green.600" fontWeight="600">₹ {Number(f.next).toLocaleString("en-IN")}</Text>
                            </Flex>
                          ))
                        )}
                      </Box>
                    </Box>
                  );
                })}
              </Box>

            )}
          </ModalBody>
        </ModalContent>
      </Modal>
      {/* <AlertCard title="Minimum Salary Validation" isLoading={false} isOpen={!!alertConfig} onClose={() => handleInternAlertClose()} onConfirm={() => { if (alertConfig?.onConfirm) alertConfig.onConfirm(); setAlertConfig(null); }} description={alertConfig?.description || ''} colorScheme={alertConfig?.colorScheme || 'red'} /> */}

    </>
  );
};
export default SalaryDetail;